import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Sparkles, Brain, Wind, Target, Flame, Music, Heart, Eye, BookOpen, Leaf } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_ICONS = {
  meditation: Brain,
  breathwork: Wind,
  chakra: Target,
  kundalini: Flame,
  mantras: Music,
  yoga: Heart,
  consciousness: Eye,
  ancient_secrets: Sparkles,
  mindfulness: Leaf,
};

const DIFFICULTY_COLORS = {
  beginner: "bg-[#8FA69A]/20 text-[#2C4A3B]",
  intermediate: "bg-[#D4AF37]/20 text-[#8B7520]",
  advanced: "bg-[#C17B5F]/20 text-[#8B4530]",
};

export default function LandingPage() {
  const [categories, setCategories] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [dailyPicks, setDailyPicks] = useState([]);

  useEffect(() => {
    axios.get(`${API}/categories`).then((r) => setCategories(r.data)).catch(console.error);
    axios.get(`${API}/practices`).then((r) => {
      const practices = r.data;
      // Pick 3 featured: one secret, varied categories
      const secrets = practices.filter((p) => p.is_secret);
      const nonSecrets = practices.filter((p) => !p.is_secret);
      const picks = [...secrets.slice(0, 1), ...nonSecrets.slice(0, 2)];
      setFeatured(picks);
    }).catch(console.error);
    axios.get(`${API}/daily-recommendation`).then((r) => setDailyPicks(r.data)).catch(console.error);
  }, []);

  return (
    <main className="min-h-screen" data-testid="landing-page">
      {/* Hero Section */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1774809553151-a6a237462b91"
            alt="Spiritual sunrise"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#F9F6F0]/30 via-[#F9F6F0]/60 to-[#F9F6F0]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-24 pb-32 md:pt-32 md:pb-40">
          <div className="max-w-3xl">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#8FA69A] mb-4 animate-fade-up">
              The Path to Cosmic Consciousness
            </p>
            <h1
              data-testid="hero-heading"
              className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-light text-[#1A221E] leading-[1.1] animate-fade-up-delay-1"
            >
              Every Sacred Practice.{" "}
              <span className="text-[#8FA69A]">Every Secret Technique.</span>
            </h1>
            <p className="mt-6 text-base md:text-lg leading-relaxed text-[#737E79] max-w-xl animate-fade-up-delay-2">
              A curated collection of the world's most powerful spiritual practices — from ancient
              Vedic meditation to Hermetic mysteries — all in one sacred space.
            </p>
            <div className="mt-8 flex flex-wrap gap-3 animate-fade-up-delay-3">
              <Link
                to="/library"
                data-testid="explore-library-btn"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#8FA69A] text-white rounded-full text-sm font-medium transition-all duration-300 hover:bg-[#2C4A3B] hover:shadow-lg"
              >
                <BookOpen className="w-4 h-4" strokeWidth={1.5} />
                Explore the Library
              </Link>
              <Link
                to="/dashboard"
                data-testid="view-journey-btn"
                className="inline-flex items-center gap-2 px-6 py-3 border border-[#8FA69A] text-[#2C4A3B] rounded-full text-sm font-medium transition-all duration-300 hover:bg-[#EAE2D6]"
              >
                Your Journey
                <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24" data-testid="categories-section">
        <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#D4AF37] mb-3">
          Pathways to Awakening
        </p>
        <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-[#1A221E]">
          Choose Your Path
        </h2>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {categories.map((cat, i) => {
            const Icon = CATEGORY_ICONS[cat.id] || Sparkles;
            return (
              <Link
                key={cat.id}
                to={`/library?category=${cat.id}`}
                data-testid={`category-card-${cat.id}`}
                className="group relative p-6 rounded-2xl border border-[#E3DDD2] bg-white hover:shadow-md transition-all duration-300 hover:-translate-y-0.5"
                style={{ animationDelay: `${i * 0.05}s` }}
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-[#EAE2D6] flex items-center justify-center shrink-0 group-hover:bg-[#8FA69A] group-hover:text-white transition-colors duration-300">
                    <Icon className="w-5 h-5" strokeWidth={1.5} />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-base font-medium text-[#1A221E] group-hover:text-[#2C4A3B] transition-colors">
                      {cat.name}
                    </h3>
                    <p className="mt-1 text-sm text-[#737E79] leading-relaxed line-clamp-2">
                      {cat.description}
                    </p>
                    <p className="mt-2 text-xs text-[#8FA69A] font-medium">
                      {cat.practice_count} practice{cat.practice_count !== 1 ? "s" : ""}
                    </p>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Daily Recommendations */}
      {dailyPicks.length > 0 && (
        <section
          className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24"
          data-testid="daily-picks-section"
        >
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#8FA69A] mb-3">
            Today's Guidance
          </p>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-[#1A221E]">
            Daily Practice Picks
          </h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            {dailyPicks.map((practice) => {
              const Icon = CATEGORY_ICONS[practice.category] || Sparkles;
              return (
                <Link
                  key={practice.id}
                  to={`/practice/${practice.id}`}
                  data-testid={`daily-pick-${practice.id}`}
                  className="group relative overflow-hidden rounded-2xl bg-white border border-[#E3DDD2] hover:shadow-md transition-all duration-300 hover:-translate-y-0.5"
                >
                  <div className="aspect-[16/9] overflow-hidden">
                    <img
                      src={practice.image_url}
                      alt={practice.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Badge className={`${DIFFICULTY_COLORS[practice.difficulty]} border-0 rounded-full text-xs`}>
                        {practice.difficulty}
                      </Badge>
                      {practice.is_secret && (
                        <Badge className="bg-[#D4AF37]/20 text-[#8B7520] border-0 rounded-full text-xs">
                          Secret
                        </Badge>
                      )}
                    </div>
                    <h3 className="font-heading text-lg font-medium text-[#1A221E] leading-snug">
                      {practice.title}
                    </h3>
                    <p className="mt-2 text-sm text-[#737E79] leading-relaxed line-clamp-2">
                      {practice.description}
                    </p>
                    <div className="mt-4 flex items-center gap-2 text-xs text-[#8FA69A]">
                      <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />
                      <span className="capitalize">{practice.category.replace("_", " ")}</span>
                      <span className="text-[#E3DDD2]">|</span>
                      <span>{practice.duration_minutes} min</span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* Featured Practices */}
      {featured.length > 0 && (
        <section
          className="relative py-16 md:py-24"
          data-testid="featured-section"
        >
          <div className="absolute inset-0">
            <img
              src="https://images.unsplash.com/photo-1635833742576-c9c12bb2f991"
              alt="Cosmic sky"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-[#1A221E]/80 to-[#1A221E]/90" />
          </div>
          <div className="relative max-w-7xl mx-auto px-6 md:px-12">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#D4AF37] mb-3">
              Hidden Wisdom
            </p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-white">
              Featured Practices
            </h2>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
              {featured.map((practice) => (
                <Link
                  key={practice.id}
                  to={`/practice/${practice.id}`}
                  data-testid={`featured-practice-${practice.id}`}
                  className="group p-6 rounded-2xl bg-white/10 backdrop-blur-md border border-white/10 hover:bg-white/15 transition-all duration-300"
                >
                  <div className="flex items-center gap-2 mb-3">
                    <Badge className={`${DIFFICULTY_COLORS[practice.difficulty]} border-0 rounded-full text-xs`}>
                      {practice.difficulty}
                    </Badge>
                    {practice.is_secret && (
                      <Badge className="bg-[#D4AF37]/30 text-[#D4AF37] border-0 rounded-full text-xs">
                        Secret Technique
                      </Badge>
                    )}
                  </div>
                  <h3 className="font-heading text-lg font-medium text-white leading-snug">
                    {practice.title}
                  </h3>
                  <p className="mt-2 text-sm text-white/70 leading-relaxed line-clamp-3">
                    {practice.overview}
                  </p>
                  <div className="mt-4 flex items-center gap-1 text-[#D4AF37] text-xs font-medium group-hover:gap-2 transition-all">
                    Begin Practice <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="max-w-7xl mx-auto px-6 md:px-12 py-12 border-t border-[#E3DDD2]" data-testid="footer">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
            <span className="font-heading text-sm text-[#737E79]">Awakening Path</span>
          </div>
          <p className="text-xs text-[#737E79]">
            Ancient wisdom, modern practice. Walk the path with presence.
          </p>
        </div>
      </footer>
    </main>
  );
}
